import { useState } from "react"
import classNames from "classnames"
import JoinCard from "../components/JoinCard"
import { TotalSteps } from "../joinSteps"

export interface Profile {
  email: string
  first_name: string
  last_name: string
  amc_email: string
  amc_password: string
}

interface Props {
  onSubmit: (profile: Profile) => Promise<void>
  error: string
  clearError: () => void
}

export default function ProfileStep({ onSubmit, error, clearError }: Props) {
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [amcEmail, setAmcEmail] = useState("")
  const [amcPassword, setAmcPassword] = useState("")
  const [sending, setSending] = useState(false)

  const emailValid = /\S+@\S+\.\S+/.test(email)
  const amcEmailValid = /\S+@\S+\.\S+/.test(amcEmail)
  const valid =
    emailValid &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    amcEmailValid &&
    amcPassword.length > 0

  const submit = async () => {
    setSending(true)
    await onSubmit({
      email,
      first_name: firstName,
      last_name: lastName,
      amc_email: amcEmail,
      amc_password: amcPassword,
    })
    setSending(false)
  }

  const change = (setter: (value: string) => void) => (value: string) => {
    setter(value)
    clearError()
  }

  return (
    <JoinCard
      title="Confirm your details"
      subtitle="Make sure your name is right, and connect your AMC account so we can book under it."
      step={3}
      totalSteps={TotalSteps}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (valid && !sending) submit()
        }}
      >
        <label className="form-label join-label" htmlFor="profile-first-name">
          First name
        </label>
        <input
          id="profile-first-name"
          className={classNames("form-control form-control-lg", { "is-invalid": !!error })}
          type="text"
          autoComplete="given-name"
          placeholder="First name"
          value={firstName}
          disabled={sending}
          onChange={(e) => change(setFirstName)(e.target.value)}
        />

        <label className="form-label join-label mt-3" htmlFor="profile-last-name">
          Last name
        </label>
        <input
          id="profile-last-name"
          className={classNames("form-control form-control-lg", { "is-invalid": !!error })}
          type="text"
          autoComplete="family-name"
          placeholder="Last name"
          value={lastName}
          disabled={sending}
          onChange={(e) => change(setLastName)(e.target.value)}
        />

        <label className="form-label join-label mt-3" htmlFor="profile-email">
          Email
        </label>
        <input
          id="profile-email"
          className={classNames("form-control form-control-lg", { "is-invalid": !!error })}
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          disabled={sending}
          onChange={(e) => change(setEmail)(e.target.value)}
        />

        <label className="form-label join-label mt-3" htmlFor="profile-amc-email">
          AMC email
        </label>
        <input
          id="profile-amc-email"
          className={classNames("form-control form-control-lg", { "is-invalid": !!error })}
          type="email"
          autoComplete="off"
          placeholder="AMC account email"
          value={amcEmail}
          disabled={sending}
          onChange={(e) => change(setAmcEmail)(e.target.value)}
        />

        <label className="form-label join-label mt-3" htmlFor="profile-amc-password">
          AMC password
        </label>
        <input
          id="profile-amc-password"
          className={classNames("form-control form-control-lg", { "is-invalid": !!error })}
          type="password"
          autoComplete="off"
          placeholder="AMC account password"
          value={amcPassword}
          disabled={sending}
          onChange={(e) => change(setAmcPassword)(e.target.value)}
        />
        {error && <div className="invalid-feedback d-block">{error}</div>}

        <button
          className="btn btn-primary btn-lg w-100 mt-3"
          type="submit"
          disabled={!valid || sending}
        >
          {sending ? "Submitting..." : "Submit application"}
        </button>
        <p className="join-fine-print">We only use your name to book tickets under your account.</p>
      </form>
    </JoinCard>
  )
}
